import React, { Fragment, type ReactNode } from 'react';

import styled from 'styled-components';
import Markdown from 'marked-react';

import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { Link } from '@grnhse/seedling/lib/birch/typography';

import { useChatTypewriter } from './use_chat_typewriter';
import {
  assistantMessageBubbleStyles,
  assistantMessageContentStyles,
} from './message_styles';

type Props = {
  content: string;
  animate?: boolean;
  footer?: ReactNode;
};

function safeHttpsUrl(href?: string): string {
  try {
    const url = new URL(href ?? '');
    return url.protocol === 'https:' ? href ?? '#' : '#';
  } catch {
    return '#';
  }
}

const renderer = {
  link(this: { elementId: string }, href: string, text: ReactNode) {
    const safe = safeHttpsUrl(href);
    return (
      <Link
        key={this.elementId}
        href={safe}
        rel="noopener noreferrer"
        target="_blank"
      >
        {text}
      </Link>
    );
  },
  image(this: { elementId: string }) {
    return <Fragment key={this.elementId} />;
  },
};

export function AssistantMessage({ content, animate = true, footer }: Props) {
  const debouncedContent = useChatTypewriter(content, 360, animate);

  return (
    <Container>
      <Bubble>
        <Markdown breaks renderer={renderer}>
          {debouncedContent}
        </Markdown>
      </Bubble>
      {footer}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Tokens.spacing['0.5']};
`;

const Bubble = styled.div`
  ${assistantMessageBubbleStyles}
  ${assistantMessageContentStyles}
`;
